import { Component, OnDestroy, OnInit } from '@angular/core';
import { ObservationService } from '../../../services/observation.service';
import { AuthenticationService, EntityService, moment, MultiTenantService, UserModel } from '@kaiser-software/chassis-core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { __ } from '@kaiser-software/chassis-localization';
import { ActivatedRoute, Router } from '@angular/router';
import { Moment } from 'moment';
import { take } from 'rxjs/operators';
import { TenantLocationService } from '../../../../locations/services/tenant-location.service';
import { CategoryService } from '../../../services/category.service';

interface DateInterval {
    start: string;
    end: string;
}

@Component({
    selector: 'app-observations-index',
    templateUrl: 'index.component.html',
    styleUrls: ['index.component.scss']
})
export class ObservationsIndexComponent implements OnInit, OnDestroy {

    protected title = 'Observations';

    protected routePrefix = 'observations';

    protected pluginPrefix = 'eyewatch-observations';

    protected table$ = new ReplaySubject(1);

    protected user$: Observable<UserModel>;

    protected dateInterval$ = new ReplaySubject<DateInterval>(1);

    protected dateIntervalName$ = new ReplaySubject<string>(1);

    public instance$ = new ReplaySubject<Promise<DataTables.Api>>(1);

    protected subscription = new Subscription();

    public startDate: string;
    public endDate: string;

    constructor(
        protected observationService: ObservationService,
        protected authenticationService: AuthenticationService,
        protected entityService: EntityService,
        protected router: Router,
        protected multiTenantService: MultiTenantService,
        protected route: ActivatedRoute,
        protected tenantLocationService: TenantLocationService,
        protected categoryService: CategoryService
    ) {
        this.user$ = this.authenticationService.user$;
    }

    public ngOnInit(): void {
        if (!this.route.snapshot.queryParams.search) {
            this.filterDate('last-24-hours');
        }

        this.subscription.add(
            this.dateInterval$
                .subscribe((interval) => {
                    this.instance$.pipe(take(1)).subscribe((instancePromise: Promise<DataTables.Api>) => {
                        instancePromise.then((instance: DataTables.Api) => {
                            instance.ajax.url(
                                this.observationService.all(true, {
                                    datatable: true,
                                    startDate: interval.start,
                                    endDate: interval.end
                                })
                            );
                            instance.ajax.reload();
                        });
                    });
                })
        );

        this.createDatatable();
    }

    public refreshDatatable() {
        if (this.startDate && this.endDate) {
            this.dateInterval$.next({
                start: this.startDate,
                end: this.endDate,
            });
        }
    }

    protected createDatatable(): void {
        this.subscription.add(
            this.user$
                .subscribe((user) => {
                    const columns = [];

                    if (this.canViewIdColumn(user)) {
                        columns.push({
                            name: 'id',
                            data: 'id',
                            title: __('Id'),
                            class: 'id-column',
                            visible: true,
                            searchable: true,
                            filter: {
                                filter_type: 'text',
                                filter_delay: 1000,
                            }
                        });
                    }

                    if (this.canViewLocationColumn(user)) {
                        columns.push({
                            name: 'location',
                            data: 'location',
                            title: __('Location'),
                            searchable: true,
                            visible: true,
                            filter: {
                                filter_type: 'select',
                                'select_type': 'select2',
                                filter_delay: 1000,
                                // select_type_options: {
                                //     ajax: {
                                //         url: this.tenantLocationService.all(true, {tenant: this.multiTenantService.getCurrentScope()}),
                                //         searchDisplayColumn: 'name',
                                //         searchQueryColumn: 'name'
                                //     }
                                // }
                            }
                        });
                    }

                    if (this.canViewCreatedAtColumn(user)) {
                        columns.push({
                            name: 'created_at',
                            data: 'created_at',
                            title: __('Date'),
                            searchable: true,
                            visible: true,
                            // filter: {
                            //     'filter_type': 'range_date',
                            //     'datepicker_type': 'bootstrap-datetimepicker',
                            //     'date_format': 'YYYY-MM-DD'
                            // },
                        });
                    }

                    if (this.canViewMainCategoryColumn(user)) {
                        columns.push({
                            name: 'main_category',
                            data: 'main_category',
                            title: __('Main category'),
                            searchable: true,
                            visible: true,
                            filter: {
                                filter_type: 'select',
                                'select_type': 'select2',
                                filter_delay: 1000,
                                // select_type_options: {
                                //     ajax: {
                                //         url: this.categoryService.all(true),
                                //         searchDisplayColumn: 'title',
                                //         searchQueryColumn: 'title',
                                //         defaultSearchParams: {
                                //             search: {
                                //                 parent_id: 'null'
                                //             },
                                //             searchJoin: 'and'
                                //         }
                                //     }
                                // }
                            },
                        });
                    }

                    if (this.canViewCategoryCodeColumn(user)) {
                        columns.push({
                            name: 'category_code',
                            data: 'category_code',
                            title: __('Code'),
                            searchable: true,
                            visible: true,
                            filter: {
                                filter_type: 'select',
                                'select_type': 'select2',
                                filter_delay: 1000,
                            },
                        });
                    }

                    if (this.canViewCategoryColumn(user)) {
                        columns.push({
                            name: 'category',
                            data: 'category',
                            title: __('Sub category'),
                            searchable: true,
                            visible: true,
                            filter: {
                                filter_type: 'select',
                                'select_type': 'select2',
                                filter_delay: 1000,
                                // select_type_options: {
                                //     ajax: {
                                //         url: this.categoryService.all(true),
                                //         searchDisplayColumn: 'title',
                                //         searchQueryColumn: 'title',
                                //         defaultSearchParams: {
                                //             search: {
                                //                 parent_id: 'not_null'
                                //             },
                                //             searchJoin: 'and'
                                //         }
                                //     }
                                // }
                            },
                        });
                    }

                    if (this.canViewDescriptionColumn(user)) {
                        columns.push({
                            name: 'description',
                            data: 'description',
                            title: __('Description'),
                            visible: true,
                            width: '500px',
                            render: function (data: string, type: string) {
                                if (type === 'export') {
                                    return data;
                                }

                                return type !== 'export' && data && data.length > 300
                                    ? data.substr(0, 300) + ' ...'
                                    : data;
                            }
                        });
                    }

                    if (this.canViewStatusColumn(user)) {
                        columns.push({
                            name: 'status',
                            data: 'status',
                            title: __('Status'),
                            searchable: true,
                            visible: true,
                            'filter': {
                                'filter_type': 'select',
                                'select_type': 'select2',
                                'data': [
                                    {'value': 'open', 'label': __('Concept')},
                                    {'value': 'completed', 'label': __('Completed')},
                                ]
                            }
                        });
                    }

                    if (this.canViewUrgentColumn(user)) {
                        columns.push({
                            name: 'urgent',
                            data: 'urgent',
                            title: __('Urgent'),
                            searchable: true,
                            visible: true,
                            sortable: false,
                            filter: {
                                'filter_type': 'select',
                                'select_type': 'select2',
                                'data': [
                                    {'value': 1, 'label': __('Yes')},
                                    {'value': 0, 'label': __('No')},
                                ]
                            },
                        });
                    }

                    if (this.canViewSecurityGuardColumn(user)) {
                        columns.push({
                            name: 'security_guard',
                            data: 'security_guard',
                            title: __('Security guard'),
                            searchable: true,
                            visible: true,
                            filter: {
                                filter_type: 'select',
                                'select_type': 'select2',
                                filter_delay: 1000,
                            },
                        });
                    }

                    const table = {
                        ajax: this.observationService.all(true, {
                            datatable: true,
                        }),
                        service: this.observationService,
                        settings: {
                            buttons: user.hasRoleIn([
                                'admin',
                                'customer',
                            ])
                                ? [{
                                    extend: 'excel',
                                    exportOptions: {
                                        orthogonal: 'export',
                                    }
                                }]
                                : [],
                        },
                        callbacks: {},
                        columns,
                        limit: -1
                    };

                    this.table$.next(table);
                })
        );
    }

    protected canViewIdColumn(user: UserModel): boolean {
        return user.hasRoleIn([
            'admin',
        ]);
    }

    protected canViewLocationColumn(user: UserModel): boolean {
        return user.hasRoleIn([
            'admin',
            'customer'
        ]);
    }

    protected canViewCreatedAtColumn(user: UserModel): boolean {
        return true;
    }

    protected canViewMainCategoryColumn(user: UserModel): boolean {
        return true;
    }

    protected canViewCategoryCodeColumn(user: UserModel): boolean {
        return user.hasRoleIn([
            'admin',
        ]);
    }

    protected canViewCategoryColumn(user: UserModel): boolean {
        return true;
    }

    protected canViewDescriptionColumn(user: UserModel): boolean {
        return true;
    }

    protected canViewStatusColumn(user: UserModel): boolean {
        return true;
    }

    protected canViewUrgentColumn(user: UserModel): boolean {
        return this.multiTenantService.isEnabled()
            && !!this.multiTenantService.getCurrentScope();

        return true;
    }

    protected canViewSecurityGuardColumn(user: UserModel): boolean {
        return user.hasRoleIn([
            'admin',
            'customer',
        ]);
    }

    protected filterDate(interval: string): void {
        // Clear input fields
        this.startDate = null;
        this.endDate   = null;

        this.dateIntervalName$.next(interval);

        switch (interval) {
            case 'last-24-hours':
                this.startDate = moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm');
                this.endDate   = moment().format('YYYY-MM-DD HH:mm');
                break;
            case 'today':
                this.startDate = this.filterDateFormat(moment());
                this.endDate   = this.filterDateFormat(moment());

                break;
            case 'yesterday':
                this.startDate = this.filterDateFormat(moment().subtract(1, 'day'));
                this.endDate   = this.filterDateFormat(moment().subtract(1, 'day'));

                break;
            case 'last-week':
                this.startDate = this.filterDateFormat(moment().subtract(1, 'week').startOf('week'));
                this.endDate   = this.filterDateFormat(moment().subtract(1, 'week').endOf('week'));

                break;
            case 'last-month':
                this.startDate = this.filterDateFormat(moment().subtract(1, 'month').startOf('month'));
                this.endDate   = this.filterDateFormat(moment().subtract(1, 'month').endOf('month'));

                break;
        }

        this.dateInterval$.next({
            start: this.startDate,
            end: this.endDate,
        });
    }

    protected filterDateFormat(date: Moment): string {
        return date.format('YYYY-MM-DD');
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

}



// WEBPACK FOOTER //
// ./apps/eyewatch/src/app/eyewatch/observations/pages/observations/index/index.component.ts